import { cloneElement, isValidElement, memo, type ReactElement, useCallback, useRef } from 'react';
import cn from 'classnames';
import { Button, IconClose, Overlay, VStack } from '@direct-frontend/components';
import { MODAL_DEFAULT_PREVENT_SCROLL } from '../../ui/Modal';
import { Carousel, type UseCarouselController } from '../Carousel';
import { Iterator } from '../Iterator';
import styles from './ImageViewer.module.css';

export interface ImageViewerProps {
    /**
     * @title Открыт ли просмотрщик
     */
    open: boolean;

    /**
     * @title Текущий индекс изображения
     */
    currentIndex: number;

    /**
     * @title Количество изображений
     */
    count: number;

    /**
     * @title Метод для отрисовки основного изображения
     */
    renderMainImage: (index: number) => ReactElement | null;

    /**
     * @title Метод для отрисовки миниатюры
     */
    renderThumbnail: (index: number) => ReactElement | null;

    /**
     * @title Обработчик изменения индекса
     */
    onIndexChange?: (index: number) => void;

    /**
     * @title Обработчик закрытия
     */
    onClose: () => void;

    /**
     * @title CSS класс
     */
    className?: string;
}

export const ImageViewer = memo(function ImageViewer(props: ImageViewerProps) {
    const { open, currentIndex, count, renderMainImage, renderThumbnail, onIndexChange, onClose, className } = props;

    // const portalContainer = usePortalContainer();
    const carouselRef = useRef<UseCarouselController>(null);

    const handleCarouselChange = useCallback(
        (newIndex: number) => {
            onIndexChange?.(newIndex);
        },
        [onIndexChange]
    );

    const handleThumbnailClick = useCallback(
        (index: number) => {
            if (index === currentIndex) {
                return;
            }

            // Обновляем индекс через карусель
            carouselRef.current?.onIndexChange(index, 'instant');
        },
        [currentIndex]
    );

    const renderMainImageWithClasses = useCallback(
        (index: number) => {
            const element = renderMainImage(index);

            return isValidElement(element)
                ? cloneElement(element as ReactElement<React.HTMLAttributes<HTMLElement>>, {
                      key: index,
                      className: (element.props as { className?: string }).className,
                  })
                : element;
        },
        [renderMainImage]
    );

    const thumbnailsIndicators =
        count === 1 ? null : (
            <Iterator
                className={cn(styles.thumbnails, 'hidden-down-sm')}
                count={count}
                data-image-viewer-thumbnails
                renderItem={(index: number) => {
                    const element = renderThumbnail(index);

                    return isValidElement(element)
                        ? cloneElement(element as ReactElement<React.HTMLAttributes<HTMLElement>>, {
                              key: index,
                              className: cn(
                                  (element.props as { className?: string }).className,
                                  styles.thumbnail,
                                  index === currentIndex && styles.thumbnailActive
                              ),
                              onClick: () => handleThumbnailClick(index),
                              role: 'button',
                              tabIndex: 0,
                              'aria-label': `Thumbnail ${index + 1}`,
                          })
                        : null;
                }}
            />
        );

    if (!open) {
        return null;
    }

    return (
        <Overlay
            hasCloseButton
            closeButton={
                <Button
                    icon={IconClose}
                    className={styles.closeButton}
                    onClick={onClose}
                    type="button"
                    size="m"
                    color="contrast"
                    aria-label="Close image viewer"
                />
            }
            preventScroll={MODAL_DEFAULT_PREVENT_SCROLL}
            isOpen
            className={cn(styles.overlay, className)}
            onClose={onClose}
        >
            <VStack
                alignItems="center"
                justifyContent="center"
                className={styles.imagesContainer}
                width="max"
                height="max"
            >
                <Carousel
                    ref={carouselRef}
                    count={count}
                    currentIndex={currentIndex}
                    onIndexChange={handleCarouselChange}
                    className={styles.carousel}
                    controlsAlignment="none"
                    enableScrollSnap
                    renderItem={renderMainImageWithClasses}
                    indicatorsSlot={thumbnailsIndicators}
                />
            </VStack>
        </Overlay>
    );
});
